import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  BASELINE_POPULATIONS,
  POPULATION_TYPES,
} from '../matrix-step.constants';

import type { PopulationType } from '../matrix-step.constants';

type LogicCategoryOption = { id: string; name?: string };

type LogicCategory = {
  category: string;
  options?: LogicCategoryOption[];
};

type RegimeLike = {
  id?: string | number;
  population?: string | null;
  customLogicIds?: string[];
  pell?: boolean;
};

export interface PopulationCriteriaBlock {
  blockId: string;
  dimension: string | null;
  values: string[];
}

export interface PopulationCriteriaBlockErrors {
  dimension: boolean;
  values: boolean;
}

export interface SelectOption {
  value: string;
  label: string;
}

type InitialDraft = {
  populationType: PopulationType;
  population: string | null;
  criteriaBlocks: PopulationCriteriaBlock[];
};

type UsePopulationsDraftProps = {
  regime: RegimeLike | null | undefined;
  customLogicCategories?: LogicCategory[];
};

const comparePrimitive = (x: string, y: string) => (x < y ? -1 : x > y ? 1 : 0);

const serializeCriteriaBlocks = (blocks: PopulationCriteriaBlock[]): string =>
  JSON.stringify(
    blocks.map((block) => [
      block.dimension,
      [...block.values].sort(comparePrimitive),
    ])
  );

const resolveInitialCriteriaBlocks = (
  logicIds: string[],
  categories: LogicCategory[],
  createBlockId: () => string
): PopulationCriteriaBlock[] =>
  categories.reduce<PopulationCriteriaBlock[]>((blocks, cat) => {
    const matchedIds = logicIds.filter((id) =>
      cat.options?.some((opt) => opt.id === id)
    );

    return matchedIds.length
      ? [
          ...blocks,
          {
            blockId: createBlockId(),
            dimension: cat.category,
            values: matchedIds,
          },
        ]
      : blocks;
  }, []);

const getBlockErrors = (
  block: PopulationCriteriaBlock
): PopulationCriteriaBlockErrors | null => {
  if (!block.dimension) return { dimension: true, values: false };
  if (!block.values.length) return { dimension: false, values: true };

  return null;
};

export const usePopulationsDraft = ({
  regime,
  customLogicCategories = [],
}: UsePopulationsDraftProps) => {
  const [populationType, setPopulationType] = useState<PopulationType>(
    POPULATION_TYPES.BASELINE
  );
  const [baselineSelected, setBaselineSelected] = useState<string | null>(null);
  const [criteriaBlocks, setCriteriaBlocks] = useState<
    PopulationCriteriaBlock[]
  >([]);
  const [criteriaErrors, setCriteriaErrors] = useState<
    Record<string, PopulationCriteriaBlockErrors>
  >({});
  const [pell, setPell] = useState(false);

  const initialRef = useRef<InitialDraft>({
    populationType: POPULATION_TYPES.BASELINE,
    population: null,
    criteriaBlocks: [],
  });
  const pendingLogicIdsRef = useRef<string[] | null>(null);
  const categoriesRef = useRef(customLogicCategories);
  const blockIdCounterRef = useRef(0);

  categoriesRef.current = customLogicCategories;

  const createBlockId = useCallback(() => {
    blockIdCounterRef.current += 1;

    return `population-criteria-${blockIdCounterRef.current}`;
  }, []);

  useEffect(() => {
    const categories = categoriesRef.current;
    const population = regime?.population ?? null;
    const logicIds = regime?.customLogicIds ?? [];
    const type = population
      ? POPULATION_TYPES.BASELINE
      : POPULATION_TYPES.CUSTOM;
    const nextPell = regime?.pell ?? false;
    const canResolveNow = !!categories.length && !!logicIds.length;
    const blocks = canResolveNow
      ? resolveInitialCriteriaBlocks(logicIds, categories, createBlockId)
      : [];

    setPopulationType(type);
    setBaselineSelected(population);
    setCriteriaErrors({});
    setPell(nextPell);
    setCriteriaBlocks(blocks);
    pendingLogicIdsRef.current =
      !canResolveNow && !!logicIds.length ? logicIds : null;
    initialRef.current = {
      populationType: type,
      population,
      criteriaBlocks: blocks,
    };
  }, [regime?.id, createBlockId]);

  useEffect(() => {
    if (!pendingLogicIdsRef.current || !customLogicCategories.length) return;

    const blocks = resolveInitialCriteriaBlocks(
      pendingLogicIdsRef.current,
      customLogicCategories,
      createBlockId
    );

    setCriteriaBlocks(blocks);
    initialRef.current = { ...initialRef.current, criteriaBlocks: blocks };
    pendingLogicIdsRef.current = null;
  }, [customLogicCategories, createBlockId]);

  const handlePopulationTypeChange = useCallback((type: PopulationType) => {
    setPopulationType(type);

    if (type === POPULATION_TYPES.BASELINE) {
      const prevPopulation = initialRef.current.population;

      setBaselineSelected(prevPopulation ?? BASELINE_POPULATIONS[0].value);
    } else {
      setBaselineSelected(null);
    }
  }, []);

  const toggleBaseline = useCallback((value: string) => {
    setBaselineSelected(value);
  }, []);

  const clearBlockErrors = useCallback((blockId: string) => {
    setCriteriaErrors((prev) => {
      if (!prev[blockId]) return prev;

      const { [blockId]: _cleared, ...rest } = prev;

      return rest;
    });
  }, []);

  const updateBlock = useCallback(
    (
      blockId: string,
      patch: Partial<Omit<PopulationCriteriaBlock, 'blockId'>>
    ) => {
      setCriteriaBlocks((prev) =>
        prev.map((block) =>
          block.blockId === blockId ? { ...block, ...patch } : block
        )
      );
      clearBlockErrors(blockId);
    },
    [clearBlockErrors]
  );

  const addCriteriaBlock = useCallback(() => {
    setCriteriaBlocks((prev) => [
      ...prev,
      { blockId: createBlockId(), dimension: null, values: [] },
    ]);
  }, [createBlockId]);

  const removeCriteriaBlock = useCallback(
    (blockId: string) => {
      setCriteriaBlocks((prev) =>
        prev.filter((block) => block.blockId !== blockId)
      );
      clearBlockErrors(blockId);
    },
    [clearBlockErrors]
  );

  const handleBlockDimensionChange = useCallback(
    (blockId: string, dimension: string | null) =>
      updateBlock(blockId, { dimension, values: [] }),
    [updateBlock]
  );

  const handleBlockValuesChange = useCallback(
    (blockId: string, values: string[]) => updateBlock(blockId, { values }),
    [updateBlock]
  );

  const dimensionOptions = useMemo<SelectOption[]>(
    () =>
      customLogicCategories.map((cat) => ({
        value: cat.category,
        label: cat.category,
      })),
    [customLogicCategories]
  );

  const getDimensionOptionsForBlock = useCallback(
    (blockId: string): SelectOption[] => {
      const usedElsewhere = new Set(
        criteriaBlocks
          .filter((block) => block.blockId !== blockId && block.dimension)
          .map((block) => block.dimension)
      );

      return dimensionOptions.filter((opt) => !usedElsewhere.has(opt.value));
    },
    [criteriaBlocks, dimensionOptions]
  );

  const getValueOptions = useCallback(
    (dimension: string | null): SelectOption[] => {
      if (!dimension) return [];

      const category = customLogicCategories.find(
        (cat) => cat.category === dimension
      );

      return (
        category?.options?.map((opt) => ({
          value: opt.id,
          label: opt.name ?? opt.id,
        })) ?? []
      );
    },
    [customLogicCategories]
  );

  const canAddCriteriaBlock = criteriaBlocks.length < dimensionOptions.length;

  const customLogicIds = useMemo(
    () => criteriaBlocks.flatMap((block) => block.values).filter(Boolean),
    [criteriaBlocks]
  );

  const validatePopulations = useCallback((): boolean => {
    const errors = criteriaBlocks.reduce<
      Record<string, PopulationCriteriaBlockErrors>
    >((acc, block) => {
      const blockErrors = getBlockErrors(block);

      return blockErrors ? { ...acc, [block.blockId]: blockErrors } : acc;
    }, {});

    setCriteriaErrors(errors);

    return !Object.keys(errors).length;
  }, [criteriaBlocks]);

  const [savedVersion, setSavedVersion] = useState(0);

  const hasChanges = useMemo(() => {
    const init = initialRef.current;

    return (
      populationType !== init.populationType ||
      baselineSelected !== init.population ||
      serializeCriteriaBlocks(criteriaBlocks) !==
        serializeCriteriaBlocks(init.criteriaBlocks)
    );
  }, [populationType, baselineSelected, criteriaBlocks, savedVersion]);

  const resetDraft = useCallback(() => {
    const init = initialRef.current;

    setPopulationType(init.populationType);
    setBaselineSelected(init.population);
    setCriteriaBlocks(init.criteriaBlocks);
    setCriteriaErrors({});
  }, []);

  const markAsSaved = useCallback(() => {
    initialRef.current = {
      populationType,
      population: baselineSelected,
      criteriaBlocks,
    };
    setSavedVersion((v) => v + 1);
  }, [populationType, baselineSelected, criteriaBlocks]);

  return {
    populationType,
    handlePopulationTypeChange,
    baselineSelected,
    toggleBaseline,
    criteriaBlocks,
    criteriaErrors,
    addCriteriaBlock,
    removeCriteriaBlock,
    canAddCriteriaBlock,
    handleBlockDimensionChange,
    handleBlockValuesChange,
    getDimensionOptionsForBlock,
    getValueOptions,
    customLogicIds,
    pell,
    validatePopulations,
    hasChanges,
    resetDraft,
    markAsSaved,
  };
};
